import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import {
  GDSHeadingLevel,
  HeadingText,
  HtmlToReact,
  Image,
  StackLayout,
  Box,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { MasonryButton } from '@leagueplatform/masonry-renderers';

export const NODE_TYPE_ORDER_NEW_CARD_DIGITAL_ALTERNATIVE_SCREEN =
  'orderNewCardDigitalAlternativeScreen';

type OrderNewCardDigitalAlternativeScreenProps = {
  image?: {
    url: string;
    description?: string;
    width?: string;
    height?: string;
  };
  heading: string;
  headingLevel?: GDSHeadingLevel;
  body: string;
  primaryButtonLabel: string;
  secondaryButtonLabel: string;
};

export type OrderNewCardDigitalAlternativeScreenNodeActions = {
  onLoad?: MasonryEngineNodeAction;
  onPrimaryButtonClick?: MasonryEngineNodeAction;
  onSecondaryButtonClick?: MasonryEngineNodeAction;
};

type OrderNewCardDigitalAlternativeScreenNode = MasonryEngineNode<
  typeof NODE_TYPE_ORDER_NEW_CARD_DIGITAL_ALTERNATIVE_SCREEN,
  OrderNewCardDigitalAlternativeScreenProps,
  OrderNewCardDigitalAlternativeScreenNodeActions,
  {}
>;

export type OrderNewCardDigitalAlternativeScreenRendererProps =
  MasonryEngineNodeRendererProps<OrderNewCardDigitalAlternativeScreenNode>;

export function OrderNewCardDigitalAlternativeScreenRenderer({
  image,
  heading,
  headingLevel = '3',
  body,
  primaryButtonLabel,
  secondaryButtonLabel,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  loading = false,
}: OrderNewCardDigitalAlternativeScreenRendererProps) {
  const invokeAction = useMasonryEngineActionEmitter();

  const handlePrimaryClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (onPrimaryButtonClick) {
        invokeAction({ event, ...onPrimaryButtonClick });
      }
    },
    [invokeAction, onPrimaryButtonClick],
  );

  const handleSecondaryClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (onSecondaryButtonClick) {
        invokeAction({ event, ...onSecondaryButtonClick });
      }
    },
    [invokeAction, onSecondaryButtonClick],
  );

  const hasPrimaryButton =
    primaryButtonLabel !== '' && onPrimaryButtonClick !== undefined;
  const hasSecondaryButton =
    secondaryButtonLabel !== '' && onSecondaryButtonClick !== undefined;
  const hasCTAButtons = hasPrimaryButton || hasSecondaryButton;

  return (
    <Box
      css={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box>
        <StackLayout
          spacing="$oneAndHalf"
          css={{
            alignItems: 'center',
            textAlign: 'center',
            justifyContent: 'center',
          }}
        >
          {image?.url ? (
            <Image
              src={image.url}
              alt={image.description ?? ''}
              width={image.width}
              height={image.height}
            />
          ) : null}

          <StackLayout
            spacing="$half"
            css={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <HeadingText level={headingLevel} size="md">
              {heading}
            </HeadingText>
            <HtmlToReact
              htmlString={body}
              options={{
                p: { component: ParagraphText, props: { emphasis: 'subtle' } },
              }}
            />
          </StackLayout>

          {hasCTAButtons && (
            <StackLayout
              spacing="$threeQuarters"
              css={{ justifyContent: 'center', alignItems: 'center' }}
            >
              {hasPrimaryButton && (
                <MasonryButton
                  priority="primary"
                  size="medium"
                  loading={loading}
                  onClick={handlePrimaryClick}
                  action={onPrimaryButtonClick}
                >
                  {primaryButtonLabel}
                </MasonryButton>
              )}
              {hasSecondaryButton && (
                <MasonryButton
                  priority="secondary"
                  size="medium"
                  disabled={loading}
                  onClick={handleSecondaryClick}
                  action={onSecondaryButtonClick}
                >
                  {secondaryButtonLabel}
                </MasonryButton>
              )}
            </StackLayout>
          )}
        </StackLayout>
      </Box>
    </Box>
  );
}
